import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";

import {
  FaCalendarAlt,
  FaChartBar,
  FaChevronRight,
  FaFileExcel,
  FaMoneyBillWave,
  FaReceipt,
  FaSyncAlt,
  FaWallet,
} from "react-icons/fa";

import "./FinancialReport.css";


/* ============================================================
   API
============================================================ */

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://dgps.co.in";


/* ============================================================
   FINANCIAL MONTH ORDER
   APRIL -> MARCH
============================================================ */

const FINANCIAL_MONTHS = [
  {
    short: "APR",
    name: "April",
    number: 4,
  },
  {
    short: "MAY",
    name: "May",
    number: 5,
  },
  {
    short: "JUN",
    name: "June",
    number: 6,
  },
  {
    short: "JUL",
    name: "July",
    number: 7,
  },
  {
    short: "AUG",
    name: "August",
    number: 8,
  },
  {
    short: "SEP",
    name: "September",
    number: 9,
  },
  {
    short: "OCT",
    name: "October",
    number: 10,
  },
  {
    short: "NOV",
    name: "November",
    number: 11,
  },
  {
    short: "DEC",
    name: "December",
    number: 12,
  },
  {
    short: "JAN",
    name: "January",
    number: 1,
  },
  {
    short: "FEB",
    name: "February",
    number: 2,
  },
  {
    short: "MAR",
    name: "March",
    number: 3,
  },
];


/* ============================================================
   HEADERS
============================================================ */

const getHeaders = () => {

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("auth_token") ||
    localStorage.getItem("access_token") ||
    "";

  const schoolId =
    localStorage.getItem("school_id") ||
    localStorage.getItem("schoolId") ||
    localStorage.getItem("selected_school_id") ||
    "";

  return {
    Authorization: token
      ? `Bearer ${token}`
      : "",

    "X-School-Id": schoolId,

    Accept: "application/json",
  };
};


/* ============================================================
   GET SELECTED ACADEMIC YEAR ID
============================================================ */

const getSelectedAcademicYearId = () => {

  const possibleKeys = [
    "academic_year_id",
    "academicYearId",
    "selected_academic_year_id",
    "selectedAcademicYearId",
    "current_academic_year_id",
    "currentAcademicYearId",
    "selected_year_id",
    "selectedYearId",
  ];

  for (const key of possibleKeys) {

    const value =
      localStorage.getItem(key);

    if (
      value !== null &&
      value !== undefined &&
      String(value).trim() !== ""
    ) {
      return String(value).trim();
    }
  }


  /*
   * Sometimes applications store the complete
   * academic year object in localStorage.
   */
  const objectKeys = [
    "academic_year",
    "academicYear",
    "selected_academic_year",
    "selectedAcademicYear",
    "current_academic_year",
    "currentAcademicYear",
  ];


  for (const key of objectKeys) {

    const value =
      localStorage.getItem(key);

    if (!value) {
      continue;
    }

    try {

      const parsed =
        JSON.parse(value);

      const id =
        parsed?.id ??
        parsed?.academic_year_id ??
        parsed?.academicYearId;

      if (
        id !== undefined &&
        id !== null &&
        String(id).trim() !== ""
      ) {
        return String(id).trim();
      }

    } catch (error) {

      /*
       * Ignore non-JSON values.
       */

    }
  }


  return "";
};


/* ============================================================
   MONEY
============================================================ */

const money = (value) => {

  return new Intl.NumberFormat(
    "en-IN",
    {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }
  ).format(
    Number(value || 0)
  );
};


/* ============================================================
   NUMBER
============================================================ */

const numberValue = (value) => {

  const num =
    Number(value || 0);

  return Number.isFinite(num)
    ? num
    : 0;
};


/* ============================================================
   RESULT
============================================================ */

const resultText = (type) => {

  if (type === "profit") {
    return "Profit";
  }

  if (type === "loss") {
    return "Loss";
  }

  return "Break Even";
};


/* ============================================================
   PARSE FINANCIAL YEAR
============================================================ */

const parseFinancialYear = (
  sessionName
) => {

  const text =
    String(
      sessionName || ""
    ).trim();


  const match =
    text.match(
      /^(\d{4})\s*-\s*(\d{4})$/
    );


  if (match) {

    return {
      startYear:
        Number(match[1]),

      endYear:
        Number(match[2]),
    };

  }


  /*
   * Fallback.
   *
   * Financial year starts in April.
   */

  const now =
    new Date();

  const currentYear =
    now.getFullYear();

  const currentMonth =
    now.getMonth() + 1;


  const startYear =
    currentMonth >= 4
      ? currentYear
      : currentYear - 1;


  return {

    startYear,

    endYear:
      startYear + 1,

  };

};


/* ============================================================
   CURRENT FINANCIAL YEAR
============================================================ */

const getCurrentFinancialYear = () => {

  const now =
    new Date();

  const year =
    now.getFullYear();

  const month =
    now.getMonth() + 1;


  if (month >= 4) {

    return {
      startYear: year,
      endYear: year + 1,
    };

  }


  return {
    startYear: year - 1,
    endYear: year,
  };

};


/* ============================================================
   GET MONTH SHORT
============================================================ */

const getMonthShort = (
  row
) => {

  const value =
    row?.month_short ||
    row?.short ||
    row?.monthShort ||
    row?.month_name ||
    row?.monthName ||
    "";


  const text =
    String(value)
      .trim()
      .toUpperCase();


  const found =
    FINANCIAL_MONTHS.find(
      (item) =>
        item.short ===
        text
    );


  if (found) {
    return found.short;
  }


  /*
   * Handle full month names.
   */

  const fullName =
    FINANCIAL_MONTHS.find(
      (item) =>
        item.name.toUpperCase() ===
        text
    );


  return fullName?.short || "";
};


/* ============================================================
   GET RAW YEAR / MONTH
============================================================ */

const getRawDateInfo = (
  row
) => {

  const short =
    getMonthShort(row);


  let year =
    numberValue(
      row?.year
    );


  let monthNumber =
    FINANCIAL_MONTHS.find(
      (item) =>
        item.short === short
    )?.number || 0;


  const candidates = [
    row?.month,
    row?.date,
    row?.month_date,
    row?.monthDate,
    row?.period,
    row?.period_start,
    row?.start_date,
    row?.startDate,
  ];


  for (
    const candidate of candidates
  ) {

    if (!candidate) {
      continue;
    }


    const text =
      String(candidate);


    /*
     * YYYY-MM
     * YYYY-MM-DD
     * YYYY/MM
     * YYYY/MM/DD
     */

    const match =
      text.match(
        /^(\d{4})[-/](\d{1,2})/
      );


    if (match) {

      year =
        Number(match[1]);

      monthNumber =
        Number(match[2]);

      break;

    }

  }


  return {
    short,
    year,
    monthNumber,
  };

};


/* ============================================================
   IS MONTH IN SELECTED FINANCIAL YEAR
============================================================ */

const isMonthInFinancialYear = (
  short,
  year,
  startYear,
  endYear
) => {

  const firstNineMonths = [
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];


  if (
    firstNineMonths.includes(
      short
    )
  ) {

    return (
      year === startYear
    );

  }


  return (
    year === endYear
  );

};


/* ============================================================
   BUILD 12 MONTHS
============================================================ */

const buildFinancialMonths = (
  rawMonths,
  sessionName
) => {

  const {
    startYear,
    endYear,
  } =
    parseFinancialYear(
      sessionName
    );


  const rows =
    Array.isArray(rawMonths)
      ? rawMonths
      : [];


  /*
   * Current financial year.
   */

  const currentFY =
    getCurrentFinancialYear();


  const selectedIsCurrentFY =
    startYear ===
      currentFY.startYear &&
    endYear ===
      currentFY.endYear;


  /*
   * Today.
   */

  const today =
    new Date();

  const currentCalendarYear =
    today.getFullYear();

  const currentCalendarMonth =
    today.getMonth() + 1;


  /*
   * Normalize backend rows.
   */

  const normalizedRows =
    rows.map(
      (row, index) => {

        const info =
          getRawDateInfo(row);

        return {
          row,
          index,
          ...info,
        };

      }
    );


  /*
   * Build exactly 12 financial months.
   */

  return FINANCIAL_MONTHS.map(
    (monthDef, index) => {

      /*
       * APR-DEC = start year
       *
       * JAN-MAR = end year
       */

      const expectedYear =
        index <= 8
          ? startYear
          : endYear;


      /*
       * Find exact month + year first.
       */

      let matched =
        normalizedRows.find(
          (item) =>
            item.short ===
              monthDef.short &&
            isMonthInFinancialYear(
              item.short,
              item.year,
              startYear,
              endYear
            )
        );


      /*
       * If backend sends wrong year,
       * find by month only.
       *
       * We will NEVER use backend year
       * for final display.
       */

      if (!matched) {

        matched =
          normalizedRows.find(
            (item) =>
              item.short ===
              monthDef.short
          );

      }


      /*
       * If there are exactly 12 rows but
       * month_short is missing, use position.
       */

      if (
        !matched &&
        normalizedRows.length === 12
      ) {

        matched =
          normalizedRows[index];

      }


      const source =
        matched?.row || {};


      /*
       * --------------------------------------------------------
       * IMPORTANT
       *
       * Final financial-year month/date is CREATED HERE.
       *
       * Therefore:
       *
       * 2025-2026
       * JAN = 2026
       * FEB = 2026
       * MAR = 2026
       *
       * 2026-2027
       * JAN = 2027
       * FEB = 2027
       * MAR = 2027
       * --------------------------------------------------------
       */

      const monthKey =
        `${expectedYear}-${String(
          monthDef.number
        ).padStart(2, "0")}`;


      /*
       * Determine whether this month has happened.
       *
       * Historical FY:
       * all 12 months are actual.
       *
       * Current FY:
       * only current/past calendar months are actual.
       *
       * Future FY:
       * all 12 are upcoming.
       */

      let isAccrued = true;


      if (selectedIsCurrentFY) {

        if (
          expectedYear >
            currentCalendarYear
        ) {

          isAccrued = false;

        } else if (
          expectedYear ===
            currentCalendarYear &&
          monthDef.number >
            currentCalendarMonth
        ) {

          isAccrued = false;

        }

      } else if (
        startYear >
        currentFY.startYear
      ) {

        /*
         * Future financial year.
         */

        isAccrued = false;

      }


      /*
       * VERY IMPORTANT:
       *
       * If a month is future, its backend values
       * MUST NOT be displayed or added to total.
       *
       * This fixes the problem where current FY
       * was showing Jan/Feb/Mar values.
       */

      const collection =
        isAccrued
          ? numberValue(
              source.collection
            )
          : 0;


      const payrollSalary =
        isAccrued
          ? numberValue(
              source.payroll_salary
            )
          : 0;


      const manualSalary =
        isAccrued
          ? numberValue(
              source.manual_salary
            )
          : 0;


      const salaryExpense =
        isAccrued
          ? numberValue(
              source.salary_expense
            )
          : (
              payrollSalary +
              manualSalary
            );


      const otherExpense =
        isAccrued
          ? numberValue(
              source.other_expense
            )
          : 0;


      /*
       * Prefer backend total expense for accrued rows.
       * But if backend total is missing/zero while
       * salary/other expense exists, calculate it.
       */

      let totalExpense =
        isAccrued
          ? numberValue(
              source.total_expense
            )
          : 0;


      if (
        isAccrued &&
        totalExpense === 0 &&
        (
          salaryExpense !== 0 ||
          otherExpense !== 0
        )
      ) {

        totalExpense =
          salaryExpense +
          otherExpense;

      }


      /*
       * Always calculate frontend net.
       */

      const netProfitLoss =
        collection -
        totalExpense;


      let resultType =
        "break_even";


      if (
        netProfitLoss > 0
      ) {

        resultType =
          "profit";

      } else if (
        netProfitLoss < 0
      ) {

        resultType =
          "loss";

      }


      return {

        /*
         * Month information
         */

        month_short:
          monthDef.short,

        month_name:
          monthDef.name,

        month:
          monthKey,

        year:
          expectedYear,


        /*
         * Financial values
         */

        collection,

        payroll_salary:
          payrollSalary,

        manual_salary:
          manualSalary,

        salary_expense:
          salaryExpense,

        other_expense:
          otherExpense,

        total_expense:
          totalExpense,

        net_profit_loss:
          netProfitLoss,


        /*
         * Status
         */

        is_accrued:
          isAccrued,

        result_type:
          resultType,

      };

    }
  );

};


/* ============================================================
   MAIN COMPONENT
============================================================ */

const FinancialReport = () => {

  const navigate =
    useNavigate();


  /* ============================================================
     STATE
  ============================================================ */

  const [data, setData] =
    useState({

      academic_year:
        null,

      months:
        [],

      grand_total: {

        collection:
          0,

        payroll_salary:
          0,

        manual_salary:
          0,

        salary_expense:
          0,

        other_expense:
          0,

        total_expense:
          0,

        net_profit_loss:
          0,

        result_type:
          "break_even",

      },

    });


  const [loading, setLoading] =
    useState(true);


  const [error, setError] =
    useState("");


  const [academicYearId, setAcademicYearId] =
    useState(
      getSelectedAcademicYearId()
    );


  /* ============================================================
     LOAD REPORT
  ============================================================ */

  const loadReport = useCallback(
    async (
      forcedAcademicYearId = ""
    ) => {

      try {

        setLoading(true);

        setError("");


        const selectedId =
          String(
            forcedAcademicYearId ||
            getSelectedAcademicYearId() ||
            academicYearId ||
            ""
          ).trim();


        /*
         * API URL.
         *
         * Controller change is NOT required.
         * We are only passing academic_year_id
         * from frontend.
         */

        const url =
          `${API_BASE_URL}/api/reports/profit-loss/monthly-summary`;


        const response =
          await axios.get(
            url,
            {
              headers:
                getHeaders(),

              params:
                selectedId
                  ? {
                      academic_year_id:
                        selectedId,
                    }
                  : {},
            }
          );


        if (
          response?.data?.status
        ) {

          const report =
            response.data.data ||
            {};


          /*
           * ----------------------------------------------------
           * IMPORTANT:
           *
           * Backend can return any year label.
           * We don't directly use it for chart/table.
           *
           * buildFinancialMonths() creates the correct
           * APR -> MAR financial year itself.
           * ----------------------------------------------------
           */

          const rawMonths =
            Array.isArray(
              report.months
            )
              ? report.months
              : [];


          const sessionName =
            String(
              report?.academic_year
                ?.session_name ||
              ""
            ).trim();


          const financialMonths =
            buildFinancialMonths(
              rawMonths,
              sessionName
            );


          /*
           * Calculate totals ONLY from accrued months.
           */

          const totals =
            financialMonths.reduce(
              (
                sum,
                row
              ) => {

                if (
                  !row.is_accrued
                ) {

                  return sum;

                }


                const collection =
                  numberValue(
                    row.collection
                  );


                const payrollSalary =
                  numberValue(
                    row.payroll_salary
                  );


                const manualSalary =
                  numberValue(
                    row.manual_salary
                  );


                const salaryExpense =
                  numberValue(
                    row.salary_expense
                  );


                const otherExpense =
                  numberValue(
                    row.other_expense
                  );


                const totalExpense =
                  numberValue(
                    row.total_expense
                  );


                const netProfitLoss =
                  collection -
                  totalExpense;


                sum.collection +=
                  collection;


                sum.payroll_salary +=
                  payrollSalary;


                sum.manual_salary +=
                  manualSalary;


                sum.salary_expense +=
                  salaryExpense;


                sum.other_expense +=
                  otherExpense;


                sum.total_expense +=
                  totalExpense;


                sum.net_profit_loss +=
                  netProfitLoss;


                return sum;

              },

              {

                collection:
                  0,

                payroll_salary:
                  0,

                manual_salary:
                  0,

                salary_expense:
                  0,

                other_expense:
                  0,

                total_expense:
                  0,

                net_profit_loss:
                  0,

                result_type:
                  "break_even",

              }
            );


          /*
           * Result.
           */

          if (
            totals.net_profit_loss >
            0
          ) {

            totals.result_type =
              "profit";

          } else if (
            totals.net_profit_loss <
            0
          ) {

            totals.result_type =
              "loss";

          }


          /*
           * Save.
           */

          setData({

            academic_year:
              report.academic_year ||
              null,

            months:
              financialMonths,

            grand_total:
              totals,

          });


          /*
           * Keep local selected ID in sync
           * with API response.
           */

          const returnedId =
            report?.academic_year?.id ??
            report?.academic_year
              ?.academic_year_id ??
            "";


          if (
            returnedId
          ) {

            setAcademicYearId(
              String(returnedId)
            );

          }

        } else {

          setError(
            response?.data?.message ||
            "Unable to load financial report."
          );

        }

      } catch (err) {

        console.error(
          "Financial report error:",
          err
        );


        setError(
          err?.response?.data?.message ||
          "Unable to load financial report."
        );

      } finally {

        setLoading(false);

      }

    },
    [
      academicYearId,
    ]
  );


  /* ============================================================
     INITIAL LOAD
============================================================ */

  useEffect(() => {

    loadReport();

  }, [
    loadReport,
  ]);


  /* ============================================================
     WATCH ACADEMIC YEAR CHANGES
============================================================ */

  useEffect(() => {

    /*
     * Same-tab academic-year selectors commonly dispatch
     * one of these custom events.
     */

    const handleAcademicYearChange = (
      event
    ) => {

      const eventId =
        event?.detail?.academicYearId ??
        event?.detail?.academic_year_id ??
        event?.detail?.id ??
        "";


      const newId =
        String(
          eventId ||
          getSelectedAcademicYearId() ||
          ""
        ).trim();


      setAcademicYearId(
        newId
      );


      loadReport(
        newId
      );

    };


    const handleStorage = (
      event
    ) => {

      const watchedKeys = [
        "academic_year_id",
        "academicYearId",
        "selected_academic_year_id",
        "selectedAcademicYearId",
        "current_academic_year_id",
        "currentAcademicYearId",
        "selected_year_id",
        "selectedYearId",
      ];


      if (
        watchedKeys.includes(
          event.key
        )
      ) {

        const newId =
          getSelectedAcademicYearId();


        setAcademicYearId(
          newId
        );


        loadReport(
          newId
        );

      }

    };


    window.addEventListener(
      "academicYearChanged",
      handleAcademicYearChange
    );

    window.addEventListener(
      "academic_year_changed",
      handleAcademicYearChange
    );

    window.addEventListener(
      "academic-year-changed",
      handleAcademicYearChange
    );

    window.addEventListener(
      "storage",
      handleStorage
    );


    return () => {

      window.removeEventListener(
        "academicYearChanged",
        handleAcademicYearChange
      );

      window.removeEventListener(
        "academic_year_changed",
        handleAcademicYearChange
      );

      window.removeEventListener(
        "academic-year-changed",
        handleAcademicYearChange
      );

      window.removeEventListener(
        "storage",
        handleStorage
      );

    };

  }, [
    loadReport,
  ]);


  /* ============================================================
     MONTHS
============================================================ */

  const months =
    useMemo(
      () => {

        return Array.isArray(
          data.months
        )
          ? data.months
          : [];

      },
      [
        data.months,
      ]
    );


  const totals =
    data.grand_total || {};


  /* ============================================================
     TOTALS
============================================================ */

  const totalIncome =
    numberValue(
      totals.collection
    );


  const totalExpense =
    numberValue(
      totals.total_expense
    );


  const netProfit =
    numberValue(
      totals.net_profit_loss
    );


  const profitMargin =
    totalIncome > 0
      ? (
          (
            netProfit /
            totalIncome
          ) *
          100
        )
      : 0;


  /* ============================================================
     MAX CHART VALUE
============================================================ */

  const maxBarValue =
    useMemo(() => {

      if (
        !months.length
      ) {

        return 1;

      }


      let max =
        0;


      months.forEach(
        (row) => {

          const income =
            numberValue(
              row.collection
            );


          const expense =
            numberValue(
              row.total_expense
            );


          max =
            Math.max(
              max,
              income,
              expense
            );

        }
      );


      return max > 0
        ? max
        : 1;

    }, [
      months,
    ]);


  /* ============================================================
     OPEN MONTH
============================================================ */

  const openMonth = (
    row
  ) => {

    if (
      !row?.month ||
      !row?.is_accrued
    ) {

      return;

    }


    navigate(
      `/admin/reports/profit-loss/${row.month}`,
      {
        state: {

          monthName:
            `${row.month_name} ${row.year}`,

        },
      }
    );

  };


  /* ============================================================
     EXCEL
============================================================ */

  const exportExcel = () => {

    if (
      !months.length
    ) {

      return;

    }


    const rows =
      months.map(
        (
          row,
          index
        ) => ({

          "#":
            index + 1,

          Month:
            `${row.month_name} ${row.year}`,

          Income:
            numberValue(
              row.collection
            ),

          "Payroll Salary":
            numberValue(
              row.payroll_salary
            ),

          "Manual Salary":
            numberValue(
              row.manual_salary
            ),

          "Total Salary":
            numberValue(
              row.salary_expense
            ),

          "Other Expense":
            numberValue(
              row.other_expense
            ),

          "Total Expense":
            numberValue(
              row.total_expense
            ),

          "Net Profit / Loss":
            numberValue(
              row.net_profit_loss
            ),

          Result:
            row.is_accrued
              ? resultText(
                  row.result_type
                )
              : "Upcoming",

        })
      );


    rows.push({

      "#":
        "",

      Month:
        "FINANCIAL YEAR TOTAL",

      Income:
        totalIncome,

      "Payroll Salary":
        numberValue(
          totals.payroll_salary
        ),

      "Manual Salary":
        numberValue(
          totals.manual_salary
        ),

      "Total Salary":
        numberValue(
          totals.salary_expense
        ),

      "Other Expense":
        numberValue(
          totals.other_expense
        ),

      "Total Expense":
        totalExpense,

      "Net Profit / Loss":
        netProfit,

      Result:
        resultText(
          totals.result_type
        ),

    });


    const worksheet =
      XLSX.utils.json_to_sheet(
        rows
      );


    worksheet["!cols"] = [

      {
        wch: 7,
      },

      {
        wch: 24,
      },

      {
        wch: 18,
      },

      {
        wch: 18,
      },

      {
        wch: 18,
      },

      {
        wch: 18,
      },

      {
        wch: 18,
      },

      {
        wch: 18,
      },

      {
        wch: 22,
      },

      {
        wch: 15,
      },

    ];


    const workbook =
      XLSX.utils.book_new();


    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Financial Report"
    );


    const yearName =
      String(
        data
          .academic_year
          ?.session_name ||
        "Financial_Year"
      ).replace(
        /[^a-zA-Z0-9_-]/g,
        "_"
      );


    XLSX.writeFile(
      workbook,
      `Financial_Report_${yearName}.xlsx`
    );

  };


  /* ============================================================
     RENDER
============================================================ */

  return (

    <div className="fr-page">


      {/* ======================================================
         HEADER
      ====================================================== */}

      <div className="fr-page-header">

        <div className="fr-heading">

          <div className="fr-title-icon">

            <FaChartBar />

          </div>


          <div>

            <h1>
              Financial Report
            </h1>

            <p>
              Financial year income,
              expense and net profit/loss
            </p>

          </div>

        </div>


        <div className="fr-actions">

          <button
            type="button"
            className="fr-excel-btn"
            onClick={
              exportExcel
            }
            disabled={
              loading ||
              !months.length
            }
          >

            <FaFileExcel />

            Excel

          </button>


          <button
            type="button"
            className="fr-refresh-btn"
            onClick={
              () =>
                loadReport()
            }
            disabled={
              loading
            }
          >

            <FaSyncAlt
              className={
                loading
                  ? "fr-spin"
                  : ""
              }
            />

            Refresh

          </button>

        </div>

      </div>


      {/* ======================================================
         FINANCIAL YEAR
      ====================================================== */}

      {data.academic_year && (

        <div className="fr-session-card">

          <div className="fr-session-icon">

            <FaCalendarAlt />

          </div>


          <div>

            <span>
              Financial Year
            </span>

            <strong>
              {
                data
                  .academic_year
                  .session_name
              }
            </strong>

          </div>


          <div className="fr-session-dates">

            {(() => {

              const {
                startYear,
                endYear,
              } =
                parseFinancialYear(
                  data
                    .academic_year
                    .session_name
                );


              return (
                <>
                  <span>
                    {startYear}-04-01
                  </span>

                  <b>
                    →
                  </b>

                  <span>
                    {endYear}-03-31
                  </span>
                </>
              );

            })()}

          </div>

        </div>

      )}


      {/* ======================================================
         ERROR
      ====================================================== */}

      {error && (

        <div className="fr-error">

          {error}

        </div>

      )}


      {/* ======================================================
         SUMMARY
      ====================================================== */}

      <div className="fr-summary-grid">


        {/* INCOME */}

        <div className="fr-summary-card">

          <div className="fr-summary-icon green">

            <FaMoneyBillWave />

          </div>


          <div>

            <span>
              Total Income
            </span>

            <strong className="fr-green">

              {money(
                totalIncome
              )}

            </strong>

          </div>

        </div>


        {/* EXPENSE */}

        <div className="fr-summary-card">

          <div className="fr-summary-icon orange">

            <FaReceipt />

          </div>


          <div>

            <span>
              Total Expense
            </span>

            <strong className="fr-orange">

              {money(
                totalExpense
              )}

            </strong>

          </div>

        </div>


        {/* NET */}

        <div className="fr-summary-card">

          <div
            className={
              `fr-summary-icon ${
                netProfit >= 0
                  ? "green"
                  : "red"
              }`
            }
          >

            <FaWallet />

          </div>


          <div>

            <span>

              {netProfit >= 0
                ? "Net Profit"
                : "Net Loss"}

            </span>


            <strong
              className={
                netProfit >= 0
                  ? "fr-green"
                  : "fr-red"
              }
            >

              {money(
                netProfit
              )}

            </strong>

          </div>

        </div>


        {/* MARGIN */}

        <div className="fr-summary-card">

          <div className="fr-summary-icon blue">

            <FaChartBar />

          </div>


          <div>

            <span>
              Profit Margin
            </span>

            <strong
              className={
                profitMargin >= 0
                  ? "fr-blue"
                  : "fr-red"
              }
            >

              {profitMargin.toFixed(2)}%

            </strong>

          </div>

        </div>

      </div>


      {/* ======================================================
         RESULT
      ====================================================== */}

      <div
        className={
          `fr-result-banner ${
            netProfit >= 0
              ? "profit"
              : "loss"
          }`
        }
      >

        <div>

          <span>
            FINANCIAL YEAR RESULT
          </span>

          <h2>

            {netProfit >= 0
              ? "Net Profit"
              : "Net Loss"}

          </h2>

          <p>
            Income − Total Expense
          </p>

        </div>


        <strong>

          {money(
            netProfit
          )}

        </strong>

      </div>


      {/* ======================================================
         MONTHLY CHART
      ====================================================== */}

      <div className="fr-card">

        <div className="fr-card-head">

          <div>

            <h2>
              Monthly Financial Overview
            </h2>

            <p>
              Month-wise income,
              expense and net result
            </p>

          </div>


          <div className="fr-chart-legend">

            <span>

              <i className="income-dot" />

              Income

            </span>


            <span>

              <i className="expense-dot" />

              Expense

            </span>

          </div>

        </div>


        {loading ? (

          <div className="fr-loading">

            <div className="fr-loader" />

            Loading financial report...

          </div>

        ) : (

          <div className="fr-chart-wrap">

            {months.map(
              (row) => {

                const income =
                  numberValue(
                    row.collection
                  );


                const expense =
                  numberValue(
                    row.total_expense
                  );


                const incomeHeight =
                  row.is_accrued
                    ? Math.max(
                        4,
                        (
                          income /
                          maxBarValue
                        ) *
                        100
                      )
                    : 4;


                const expenseHeight =
                  row.is_accrued
                    ? Math.max(
                        4,
                        (
                          expense /
                          maxBarValue
                        ) *
                        100
                      )
                    : 4;


                return (

                  <div
                    className={
                      `fr-chart-month ${
                        !row.is_accrued
                          ? "future"
                          : ""
                      }`
                    }
                    key={
                      row.month
                    }
                  >


                    <div className="fr-chart-values">

                      {row.is_accrued && (

                        <span>
                          {money(
                            income
                          )}
                        </span>

                      )}

                    </div>


                    <div className="fr-bars">


                      <div className="fr-bar-column">

                        <div
                          className="fr-income-bar"
                          style={{
                            height:
                              `${incomeHeight}%`,
                          }}
                          title={
                            row.is_accrued
                              ? `Income: ${money(
                                  income
                                )}`
                              : "Upcoming"
                          }
                        />

                      </div>


                      <div className="fr-bar-column">

                        <div
                          className="fr-expense-bar"
                          style={{
                            height:
                              `${expenseHeight}%`,
                          }}
                          title={
                            row.is_accrued
                              ? `Expense: ${money(
                                  expense
                                )}`
                              : "Upcoming"
                          }
                        />

                      </div>

                    </div>


                    <div className="fr-chart-month-name">

                      <strong>
                        {
                          row.month_short
                        }
                      </strong>

                      <small>
                        {
                          row.year
                        }
                      </small>

                    </div>


                    <div
                      className={
                        `fr-chart-net ${
                          !row.is_accrued
                            ? ""
                            : numberValue(
                                row.net_profit_loss
                              ) >= 0
                              ? "profit"
                              : "loss"
                        }`
                      }
                    >

                      {row.is_accrued
                        ? money(
                            row.net_profit_loss
                          )
                        : "-"}

                    </div>

                  </div>

                );

              }
            )}

          </div>

        )}

      </div>


      {/* ======================================================
         MONTHLY TABLE
      ====================================================== */}

      <div className="fr-card">

        <div className="fr-card-head">

          <div>

            <h2>
              Monthly Financial Statement
            </h2>

            <p>
              12-month financial year
              breakdown
            </p>

          </div>

        </div>


        {loading ? (

          <div className="fr-loading">

            <div className="fr-loader" />

            Loading...

          </div>

        ) : (

          <div className="fr-table-wrap">

            <table className="fr-table">

              <thead>

                <tr>

                  <th>
                    Month
                  </th>

                  <th className="right">
                    Income
                  </th>

                  <th className="right">
                    Salary
                  </th>

                  <th className="right">
                    Other Expense
                  </th>

                  <th className="right">
                    Total Expense
                  </th>

                  <th className="right">
                    Net Profit / Loss
                  </th>

                  <th className="center">
                    Result
                  </th>

                  <th className="center">
                    Action
                  </th>

                </tr>

              </thead>


              <tbody>

                {months.map(
                  (row) => {

                    const net =
                      numberValue(
                        row.net_profit_loss
                      );


                    return (

                      <tr
                        key={
                          row.month
                        }
                        className={
                          !row.is_accrued
                            ? "fr-future-row"
                            : ""
                        }
                      >


                        {/* MONTH */}

                        <td>

                          <div className="fr-month">

                            <span className="fr-month-box">

                              {
                                row.month_short
                              }

                            </span>


                            <div>

                              <strong>

                                {
                                  row.month_name
                                }

                              </strong>

                              <small>

                                {
                                  row.year
                                }

                              </small>

                            </div>

                          </div>

                        </td>


                        {/* INCOME */}

                        <td className="right">

                          {row.is_accrued ? (

                            <strong className="fr-green">

                              {money(
                                row.collection
                              )}

                            </strong>

                          ) : (

                            <span className="fr-upcoming">
                              Upcoming
                            </span>

                          )}

                        </td>


                        {/* SALARY */}

                        <td className="right">

                          {money(
                            row.is_accrued
                              ? row.salary_expense
                              : 0
                          )}

                        </td>


                        {/* OTHER EXPENSE */}

                        <td className="right">

                          {money(
                            row.is_accrued
                              ? row.other_expense
                              : 0
                          )}

                        </td>


                        {/* TOTAL EXPENSE */}

                        <td className="right">

                          <strong className="fr-orange">

                            {money(
                              row.is_accrued
                                ? row.total_expense
                                : 0
                            )}

                          </strong>

                        </td>


                        {/* NET */}

                        <td className="right">

                          {row.is_accrued ? (

                            <strong
                              className={
                                net >= 0
                                  ? "fr-green"
                                  : "fr-red"
                              }
                            >

                              {money(
                                net
                              )}

                            </strong>

                          ) : (

                            <span className="fr-upcoming">
                              -
                            </span>

                          )}

                        </td>


                        {/* RESULT */}

                        <td className="center">

                          {row.is_accrued ? (

                            <span
                              className={
                                `fr-result-badge ${
                                  row.result_type
                                }`
                              }
                            >

                              {
                                resultText(
                                  row.result_type
                                )
                              }

                            </span>

                          ) : (

                            <span className="fr-result-badge upcoming">
                              Upcoming
                            </span>

                          )}

                        </td>


                        {/* ACTION */}

                        <td className="center">

                          <button
                            type="button"
                            className="fr-view-btn"
                            disabled={
                              !row.is_accrued
                            }
                            onClick={() =>
                              openMonth(
                                row
                              )
                            }
                          >

                            View

                            <FaChevronRight />

                          </button>

                        </td>

                      </tr>

                    );

                  }
                )}


                {!months.length && (

                  <tr>

                    <td
                      colSpan="8"
                      className="fr-empty"
                    >

                      No financial data found.

                    </td>

                  </tr>

                )}

              </tbody>


              {/* ==================================================
                 TOTAL
              ================================================== */}

              {!!months.length && (

                <tfoot>

                  <tr>

                    <td>
                      FINANCIAL YEAR TOTAL
                    </td>


                    <td className="right fr-total-green">

                      {money(
                        totalIncome
                      )}

                    </td>


                    <td className="right">

                      {money(
                        totals.salary_expense
                      )}

                    </td>


                    <td className="right">

                      {money(
                        totals.other_expense
                      )}

                    </td>


                    <td className="right fr-total-orange">

                      {money(
                        totalExpense
                      )}

                    </td>


                    <td
                      className={
                        `right ${
                          netProfit >= 0
                            ? "fr-total-green"
                            : "fr-total-red"
                        }`
                      }
                    >

                      {money(
                        netProfit
                      )}

                    </td>


                    <td className="center">

                      <span
                        className={
                          `fr-footer-result ${
                            netProfit >= 0
                              ? "profit"
                              : "loss"
                          }`
                        }
                      >

                        {
                          resultText(
                            totals.result_type
                          )
                        }

                      </span>

                    </td>


                    <td />

                  </tr>

                </tfoot>

              )}

            </table>

          </div>

        )}

      </div>

    </div>

  );

};


export default FinancialReport;